import { useState, useEffect, useMemo } from 'react';
import { LuPin, LuPlus, LuTrash2, LuX, LuUser, LuEyeOff } from 'react-icons/lu';
import {
	Upload,
	message,
	Popconfirm,
	Checkbox,
	Select,
	DatePicker,
} from 'antd';
import { Editor } from '@hugerte/hugerte-react';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { motion } from 'motion/react';
import {
	downloadFromS3Signed,
	downloadFromS3,
	uploadToS3,
	uploadImageToS3FromHugeRTE,
	ml,
} from 'src/_shared/services/utils.js';
import {
	sanitizeWithIframes,
	beforeUpload,
	lightenHex,
	accentRgba,
	darkenHex,
} from './utils.js';
import { getTagHex } from './constants.js';

function AnnouncementForm({
	announcement,
	allTags,
	onSave,
	onClose,
	onDeleteTag,
	currentUser,
	allMultiLingualData,
	userGroups,
	accentColor,
}) {
	const isEditing = Boolean(announcement);
	const [title, setTitle] = useState(announcement?.title || '');
	const [content, setContent] = useState(() =>
		sanitizeWithIframes(announcement?.content || '')
	);
	const [editorInitialValue] = useState(() =>
		sanitizeWithIframes(announcement?.content || '')
	);
	const [selectedTags, setSelectedTags] = useState(announcement?.tags || []);
	const [isPinned, setIsPinned] = useState(announcement?.pinned || false);
	const [hideFromEmployees, setHideFromEmployees] = useState(
		announcement?.hide || false
	);
	const [displayGroupSelect, setDisplayGroupSelect] = useState(
		announcement?.displayGroupSelect || false
	);
	const [selectedUserGroups, setSelectedUserGroups] = useState(() => {
		if (!announcement?.userGroups) return [];
		try {
			// Handle potentially double-stringified AWSJSON from the resolver
			let parsed = announcement.userGroups;
			while (typeof parsed === 'string') {
				parsed = JSON.parse(parsed);
			}

			if (!Array.isArray(parsed)) return [];
			// Filter to only valid {key, label, value} objects, dropping corrupted entries
			return parsed.filter(
				(g) =>
					g &&
					typeof g === 'object' &&
					g.key &&
					g.label &&
					g.value &&
					g.value !== '[]'
			);
		} catch {
			return [];
		}
	});
	const [newTag, setNewTag] = useState('');
	const [thumbnail, setThumbnail] = useState(announcement?.thumbnail || null);
	const [thumbnailUrl, setThumbnailUrl] = useState('');
	const [thumbnailChanged, setThumbnailChanged] = useState(false);
	const [editorKey] = useState(1);
	const [contentMode, setContentMode] = useState('richtext');
	const [author, setAuthor] = useState(announcement?.author || '');
	const [publishAt, setPublishAt] = useState(
		announcement?.publishAt ? dayjs(announcement.publishAt) : null
	);
	const [expireAt, setExpireAt] = useState(
		announcement?.expireAt ? dayjs(announcement.expireAt) : null
	);
	const [deleteTagTarget, setDeleteTagTarget] = useState(null);
	const [deleteTagLoading, setDeleteTagLoading] = useState(false);
	const [deleteTagSuccess, setDeleteTagSuccess] = useState(false);

	useEffect(() => {
		if (announcement?.thumbnailSignedUrl) {
			setThumbnailUrl(announcement.thumbnailSignedUrl);
		} else if (announcement?.thumbnail?.key) {
			downloadFromS3Signed(
				announcement.thumbnail.key,
				announcement.thumbnail.bucket || 'erin-images'
			).then(setThumbnailUrl);
		}
	}, [announcement?.thumbnail?.key, announcement?.thumbnail?.bucket]);

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		const handleEsc = (e) => {
			if (e.key === 'Escape') {
				if (deleteTagTarget) {
					if (!deleteTagLoading) setDeleteTagTarget(null);
				} else {
					onClose();
				}
			}
		};

		document.addEventListener('keydown', handleEsc);
		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', handleEsc);
		};
	}, [onClose, deleteTagTarget, deleteTagLoading]);

	const mergedTags = useMemo(
		() => [...new Set([...allTags, ...selectedTags])].sort(),
		[allTags, selectedTags]
	);

	const sortedUserGroups = useMemo(
		() =>
			[...userGroups].sort((a, b) =>
				(a.name || '').localeCompare(b.name || '')
			),
		[userGroups]
	);

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!title.trim() || !content.trim()) {
			message.error('Please fill in the title and content.');
			return;
		}

		const input = {
			companyId: currentUser.companyId,
			title: title.trim(),
			content,
			tags: selectedTags,
			pinned: isPinned,
			hide: hideFromEmployees,
			displayGroupSelect,
			userGroups: JSON.stringify(
				displayGroupSelect && selectedUserGroups.length > 0
					? selectedUserGroups.map((g) => ({
							key: g.key,
							label: g.label,
							value: g.value,
						}))
					: []
			),
			author: author.trim() || null,
			publishAt: publishAt ? publishAt.toISOString() : null,
			expireAt: expireAt ? expireAt.toISOString() : null,
		};

		if (isEditing) {
			input.id = announcement.id;
		}

		if (thumbnailChanged || (!isEditing && thumbnail)) {
			input.thumbnail = thumbnail;
		}

		onSave(input, isEditing);
	};

	const toggleTag = (tag) => {
		setSelectedTags((prev) =>
			prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
		);
	};

	const uploadImageHandler = (blobInfo, progress) => {
		return new Promise(async (resolve, reject) => {
			try {
				const image = blobInfo.base64();
				const file = blobInfo.blob();

				const maxBytes = 2 * 1024 * 1024;
				if (file.size > maxBytes) {
					const error = new Error('Image must be smaller than 2MB');
					message.error(error.message);
					reject(error.message);
					return;
				}

				if (typeof progress === 'function') progress(10);

				const uploadFile = await uploadImageToS3FromHugeRTE(
					image,
					file,
					`announcements/${currentUser.companyId}/${Date.now()}-${file.name}`,
					'erin-images'
				);

				if (typeof progress === 'function') progress(50);

				const link = downloadFromS3(uploadFile.key, uploadFile.bucket);

				if (typeof progress === 'function') progress(100);

				resolve(link);
			} catch (error) {
				console.error('Upload failed:', error);
				reject(error.message || 'Image upload failed');
				message.error('Image upload failed: ' + error.message);
			}
		});
	};

	const addCustomTag = () => {
		const tag = newTag.trim();
		if (tag && !selectedTags.includes(tag)) {
			setSelectedTags((prev) => [...prev, tag]);
			setNewTag('');
		}
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			onClick={onClose}
		>
			<style>{`.announcement-tag-wrapper:hover .announcement-tag-delete { opacity: 1 !important; transform: scale(1) !important; } @keyframes tagDeleteSpin { to { transform: rotate(360deg); } }`}</style>
			{/* Backdrop */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className="fixed inset-0"
				style={{
					background: 'rgba(15,23,42,0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			{/* Modal */}
			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.95 }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
				className="relative flex w-full flex-col overflow-hidden rounded-2xl bg-white"
				style={{
					maxWidth: 672,
					maxHeight: '90vh',
					boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div
					className="flex items-center justify-between p-6"
					style={{ paddingBottom: 0 }}
				>
					<h2
						style={{
							color: '#1e293b',
							fontSize: 18,
							fontWeight: 600,
							margin: 0,
						}}
					>
						{isEditing
							? ml('Edit Announcement', currentUser, allMultiLingualData)
							: ml('New Announcement', currentUser, allMultiLingualData)}
					</h2>
					<button
						type="button"
						onClick={onClose}
						style={{
							width: 32,
							height: 32,
							borderRadius: 12,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							color: '#475569',
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = '#f1f5f9';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = 'none';
						}}
					>
						<LuX size={14} />
					</button>
				</div>

				{/* Form */}
				<form
					onSubmit={handleSubmit}
					className="overflow-y-auto p-6"
					style={{
						scrollbarWidth: 'thin',
						scrollbarColor: '#cbd5e1 transparent',
					}}
				>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
						{/* Title */}
						<div>
							<label
								className="mb-1.5 block"
								style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
							>
								{ml('Title', currentUser, allMultiLingualData)}
							</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder={ml(
									'Announcement title...',
									currentUser,
									allMultiLingualData
								)}
								className="w-full rounded-xl text-sm"
								style={{
									padding: '10px 16px',
									background: '#f8fafc',
									border: '1px solid #e2e8f0',
									outline: 'none',
									color: '#334155',
									transition: 'all 0.2s',
								}}
								onFocus={(e) => {
									e.target.style.borderColor = lightenHex(accentColor);
									e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
								}}
								onBlur={(e) => {
									e.target.style.borderColor = '#e2e8f0';
									e.target.style.boxShadow = 'none';
								}}
								required
							/>
						</div>

						{/* Author */}
						<div>
							<label
								className="mb-1.5 block"
								style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
							>
								{ml('Author', currentUser, allMultiLingualData)}{' '}
								<span style={{ color: '#475569', fontWeight: 400 }}>
									({ml('optional', currentUser, allMultiLingualData)})
								</span>
							</label>
							<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
								<input
									type="text"
									value={author}
									onChange={(e) => setAuthor(e.target.value)}
									placeholder={ml(
										'Author name...',
										currentUser,
										allMultiLingualData
									)}
									style={{
										flex: 1,
										padding: '10px 16px',
										borderRadius: 12,
										background: '#f8fafc',
										border: '1px solid #e2e8f0',
										outline: 'none',
										fontSize: 14,
										color: '#334155',
										transition: 'all 0.2s',
									}}
									onFocus={(e) => {
										e.target.style.borderColor = lightenHex(accentColor);
										e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
									}}
									onBlur={(e) => {
										e.target.style.borderColor = '#e2e8f0';
										e.target.style.boxShadow = 'none';
									}}
								/>
								<button
									type="button"
									onClick={() =>
										setAuthor(
											`${get(currentUser, 'firstName', '')} ${get(currentUser, 'lastName', '')}`.trim()
										)
									}
									style={{
										padding: '8px 12px',
										borderRadius: 10,
										fontSize: 11,
										fontWeight: 600,
										color: accentColor,
										background: accentRgba(accentColor, 0.08),
										border: 'none',
										cursor: 'pointer',
										whiteSpace: 'nowrap',
										transition: 'all 0.2s',
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.background = accentRgba(
											accentColor,
											0.15
										);
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.background = accentRgba(
											accentColor,
											0.08
										);
									}}
								>
									<LuUser
										style={{
											display: 'inline',
											verticalAlign: '-2px',
											marginRight: 3,
										}}
										size={12}
									/>
									{ml('Me', currentUser, allMultiLingualData)}
								</button>
								<button
									type="button"
									onClick={() =>
										setAuthor(get(currentUser, 'company.name', ''))
									}
									style={{
										padding: '8px 12px',
										borderRadius: 10,
										fontSize: 11,
										fontWeight: 600,
										color: accentColor,
										background: accentRgba(accentColor, 0.08),
										border: 'none',
										cursor: 'pointer',
										whiteSpace: 'nowrap',
										transition: 'all 0.2s',
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.background = accentRgba(
											accentColor,
											0.15
										);
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.background = accentRgba(
											accentColor,
											0.08
										);
									}}
								>
									{ml('Company', currentUser, allMultiLingualData)}
								</button>
							</div>
						</div>

						{/* Publish & Expire dates */}
						<div style={{ display: 'flex', gap: 12 }}>
							<div style={{ flex: 1 }}>
								<label
									className="mb-1.5 block"
									style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
								>
									{ml('Publish At', currentUser, allMultiLingualData)}{' '}
									<span style={{ color: '#475569', fontWeight: 400 }}>
										({ml('optional', currentUser, allMultiLingualData)})
									</span>
								</label>
								<DatePicker
									value={publishAt}
									onChange={setPublishAt}
									showTime
									format="YYYY-MM-DD HH:mm"
									placeholder={ml(
										'Schedule publish...',
										currentUser,
										allMultiLingualData
									)}
									style={{ width: '100%', borderRadius: 12 }}
									allowClear
								/>
							</div>
							<div style={{ flex: 1 }}>
								<label
									className="mb-1.5 block"
									style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
								>
									{ml('Expire At', currentUser, allMultiLingualData)}{' '}
									<span style={{ color: '#475569', fontWeight: 400 }}>
										({ml('optional', currentUser, allMultiLingualData)})
									</span>
								</label>
								<DatePicker
									value={expireAt}
									onChange={setExpireAt}
									showTime
									format="YYYY-MM-DD HH:mm"
									placeholder={ml(
										'Set expiration...',
										currentUser,
										allMultiLingualData
									)}
									style={{ width: '100%', borderRadius: 12 }}
									allowClear
								/>
							</div>
						</div>

						{/* Thumbnail Upload */}
						<div>
							<label
								className="mb-1.5 block"
								style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
							>
								{ml('Thumbnail', currentUser, allMultiLingualData)}{' '}
								<span style={{ color: '#475569', fontWeight: 400 }}>
									({ml('optional', currentUser, allMultiLingualData)})
								</span>
							</label>
							<div className="flex items-center gap-3">
								<Upload
									showUploadList
									name="thumbnail"
									listType="picture-card"
									className={thumbnail && 'hide-upload-btn'}
									customRequest={async ({ file, onSuccess, onError }) => {
										try {
											const newThumbnail = {
												bucket: 'erin-images',
												key: `announcements/${currentUser.companyId}/${Date.now()}-${file.name}`,
												region: 'us-east-2',
											};
											await uploadToS3(
												file,
												newThumbnail.key,
												newThumbnail.bucket
											);
											const url = await downloadFromS3Signed(
												newThumbnail.key,
												'erin-images'
											);
											onSuccess(newThumbnail);
											setThumbnail(newThumbnail);
											setThumbnailChanged(true);
											setThumbnailUrl(url);
										} catch (error) {
											console.error(error);
											onError(error);
										}
									}}
									beforeUpload={beforeUpload}
									multiple={false}
									disabled={thumbnail !== null}
									fileList={
										thumbnail
											? [
													{
														uid: '-1',
														name: thumbnail.key,
														status: 'done',
														url: thumbnailUrl,
													},
												]
											: []
									}
								>
									<div
										className="flex flex-col items-center gap-1 text-xs"
										style={{ color: '#475569' }}
									>
										<LuPlus />
										{ml('Upload', currentUser, allMultiLingualData)}
									</div>
								</Upload>
								{thumbnail && (
									<Popconfirm
										title={ml(
											'Remove thumbnail?',
											currentUser,
											allMultiLingualData
										)}
										onConfirm={() => {
											setThumbnail(null);
											setThumbnailChanged(true);
											setThumbnailUrl('');
										}}
									>
										<button
											type="button"
											className="text-xs font-medium"
											style={{
												color: '#ef4444',
												background: 'none',
												border: 'none',
												cursor: 'pointer',
											}}
										>
											<LuTrash2
												style={{
													display: 'inline',
													verticalAlign: 'middle',
													marginRight: 4,
												}}
											/>
											{ml('Remove', currentUser, allMultiLingualData)}
										</button>
									</Popconfirm>
								)}
							</div>
						</div>

						{/* Tags */}
						<div>
							<label
								className="mb-1.5 block"
								style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
							>
								{ml('Tags', currentUser, allMultiLingualData)}
							</label>
							<div className="mb-2 flex flex-wrap gap-1.5">
								{mergedTags.map((tag) => {
									const isSelected = selectedTags.includes(tag);
									return (
										<div
											key={tag}
											className="announcement-tag-wrapper"
											style={{ position: 'relative', display: 'inline-flex' }}
										>
											<button
												type="button"
												onClick={() => toggleTag(tag)}
												style={{
													paddingLeft: 10,
													paddingRight: 10,
													paddingTop: 6,
													paddingBottom: 6,
													borderRadius: 8,
													border: '1px solid',
													borderColor: isSelected
														? accentRgba(getTagHex(tag), 0.3)
														: '#e2e8f0',
													background: isSelected
														? accentRgba(getTagHex(tag), 0.1)
														: '#fff',
													color: isSelected ? getTagHex(tag) : '#475569',
													cursor: 'pointer',
													transition: 'all 0.2s',
													fontSize: 11,
													fontWeight: 500,
													lineHeight: 1,
													display: 'inline-flex',
													alignItems: 'center',
												}}
											>
												{tag}
											</button>
											<button
												type="button"
												className="announcement-tag-delete"
												onClick={(e) => {
													e.stopPropagation();
													setDeleteTagTarget(tag);
													setDeleteTagLoading(false);
													setDeleteTagSuccess(false);
												}}
												style={{
													position: 'absolute',
													top: -5,
													right: -5,
													width: 16,
													height: 16,
													borderRadius: '50%',
													background: '#ef4444',
													color: '#fff',
													border: '2px solid #fff',
													cursor: 'pointer',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													padding: 0,
													lineHeight: 1,
													fontSize: 10,
													opacity: 0,
													transform: 'scale(0.5)',
													transition: 'opacity 0.15s, transform 0.15s',
													zIndex: 1,
												}}
											>
												<LuX size={8} />
											</button>
										</div>
									);
								})}
							</div>
							<div className="flex gap-2">
								<input
									type="text"
									value={newTag}
									onChange={(e) => setNewTag(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();
											addCustomTag();
										}
									}}
									placeholder={ml(
										'Add custom tag...',
										currentUser,
										allMultiLingualData
									)}
									className="rounded-lg text-xs"
									style={{
										flex: 1,
										padding: '6px 12px',
										background: '#f8fafc',
										border: '1px solid #e2e8f0',
										outline: 'none',
										color: '#334155',
										transition: 'all 0.2s',
									}}
									onFocus={(e) => {
										e.target.style.borderColor = lightenHex(accentColor);
										e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
									}}
									onBlur={(e) => {
										e.target.style.borderColor = '#e2e8f0';
										e.target.style.boxShadow = 'none';
									}}
								/>
								<button
									type="button"
									onClick={addCustomTag}
									style={{
										padding: '6px 12px',
										fontSize: 12,
										fontWeight: 600,
										color: accentColor,
										background: 'none',
										border: 'none',
										cursor: 'pointer',
										transition: 'color 0.2s',
									}}
								>
									{ml('Add', currentUser, allMultiLingualData)}
								</button>
							</div>
						</div>

						{/* Content Editor */}
						<div>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									marginBottom: 6,
								}}
							>
								<label
									style={{
										color: '#334155',
										fontSize: 14,
										fontWeight: 600,
										margin: 0,
									}}
								>
									{ml('Content', currentUser, allMultiLingualData)}
								</label>
								<div
									style={{
										display: 'flex',
										background: '#f1f5f9',
										borderRadius: 8,
										padding: 2,
									}}
								>
									{['richtext', 'html'].map((mode) => (
										<button
											key={mode}
											type="button"
											onClick={() => setContentMode(mode)}
											style={{
												padding: '4px 12px',
												borderRadius: 6,
												fontSize: 12,
												fontWeight: contentMode === mode ? 600 : 400,
												color: contentMode === mode ? accentColor : '#475569',
												background:
													contentMode === mode ? '#fff' : 'transparent',
												border: 'none',
												cursor: 'pointer',
												transition: 'all 0.2s',
												boxShadow:
													contentMode === mode
														? '0 1px 2px rgba(0,0,0,0.08)'
														: 'none',
											}}
										>
											{mode === 'richtext'
												? ml('Rich Text', currentUser, allMultiLingualData)
												: ml('HTML', currentUser, allMultiLingualData)}
										</button>
									))}
								</div>
							</div>
							{contentMode === 'richtext' ? (
								<div style={{ borderRadius: 12, overflow: 'hidden' }}>
									<Editor
										key={editorKey}
										init={{
											height: 200,
											resize: true,
											menubar: false,
											branding: false,
											plugins: ['image', 'code', 'media', 'link', 'lists'],
											toolbar:
												'undo redo | styles | fontfamily | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | media link image',
											images_upload_handler: uploadImageHandler,
											automatic_uploads: true,
											file_picker_types: 'image',
											placeholder: ml(
												'Write your announcement...',
												currentUser,
												allMultiLingualData
											),
											content_style: 'img {max-width: 100%;}',
										}}
										initialValue={editorInitialValue}
										onEditorChange={setContent}
									/>
								</div>
							) : (
								<textarea
									value={content}
									onChange={(e) => setContent(e.target.value)}
									placeholder={ml(
										'Enter HTML content...',
										currentUser,
										allMultiLingualData
									)}
									style={{
										width: '100%',
										height: 194,
										padding: '12px 14px',
										borderRadius: 12,
										border: '1px solid #e2e8f0',
										background: '#f8fafc',
										outline: 'none',
										fontSize: 13,
										fontFamily: 'monospace',
										color: '#334155',
										resize: 'vertical',
										boxSizing: 'border-box',
										transition: 'all 0.2s',
									}}
									onFocus={(e) => {
										e.target.style.borderColor = lightenHex(accentColor);
										e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
									}}
									onBlur={(e) => {
										e.target.style.borderColor = '#e2e8f0';
										e.target.style.boxShadow = 'none';
									}}
								/>
							)}
						</div>

						{/* Options */}
						<div
							className="flex flex-wrap items-center gap-4 rounded-xl p-4"
							style={{ background: '#f8fafc' }}
						>
							{/* Pin toggle */}
							<div className="flex items-center gap-3">
								<button
									type="button"
									onClick={() => setIsPinned(!isPinned)}
									style={{
										position: 'relative',
										width: 44,
										height: 24,
										borderRadius: 9999,
										background: isPinned ? lightenHex(accentColor) : '#cbd5e1',
										border: 'none',
										outline: 'none',
										cursor: 'pointer',
										transition: 'background 0.2s',
										padding: 0,
									}}
								>
									<span
										style={{
											position: 'absolute',
											top: 2,
											left: 2,
											width: 20,
											height: 20,
											borderRadius: 9999,
											background: '#fff',
											boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
											transition: 'transform 0.2s',
											transform: isPinned
												? 'translateX(20px)'
												: 'translateX(0)',
										}}
									/>
								</button>
								<div>
									<p
										className="text-sm font-medium"
										style={{ color: '#334155', margin: 0 }}
									>
										<LuPin
											style={{
												display: 'inline',
												verticalAlign: '-2px',
												marginRight: 4,
											}}
										/>
										{ml('Pin to top', currentUser, allMultiLingualData)}
									</p>
								</div>
							</div>

							{/* Hide from employees */}
							<Checkbox
								checked={hideFromEmployees}
								onChange={(e) => setHideFromEmployees(e.target.checked)}
							>
								<span className="text-sm" style={{ color: '#334155' }}>
									<LuEyeOff
										style={{
											display: 'inline',
											verticalAlign: '-2px',
											marginRight: 4,
										}}
									/>
									{ml('Hide from employees', currentUser, allMultiLingualData)}
								</span>
							</Checkbox>

							{/* Restrict to groups */}
							<Checkbox
								checked={displayGroupSelect}
								onChange={(e) => setDisplayGroupSelect(e.target.checked)}
							>
								<span className="text-sm" style={{ color: '#334155' }}>
									{ml('Restrict to groups', currentUser, allMultiLingualData)}
								</span>
							</Checkbox>
						</div>

						{/* User Group Select */}
						{displayGroupSelect && (
							<div>
								<label
									className="mb-1.5 block"
									style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
								>
									{ml(
										'Choose Employee Group(s)',
										currentUser,
										allMultiLingualData
									)}
								</label>
								<Select
									showSearch
									labelInValue
									mode="multiple"
									placeholder={ml(
										'Select groups',
										currentUser,
										allMultiLingualData
									)}
									filterOption={(input, option) =>
										option.children?.toLowerCase().includes(input.toLowerCase())
									}
									style={{ width: '100%', borderRadius: 12 }}
									value={selectedUserGroups}
									onChange={(val) => setSelectedUserGroups(val)}
								>
									{sortedUserGroups.map((group) => (
										<Select.Option key={group.id} value={group.id}>{group.name}</Select.Option>
									))}
								</Select>
							</div>
						)}

						{/* Action buttons */}
						<div className="flex gap-3 pt-2">
							<button
								type="button"
								onClick={onClose}
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									background: '#f1f5f9',
									color: '#475569',
									border: 'none',
									cursor: 'pointer',
									transition: 'background 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = '#e2e8f0';
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = '#f1f5f9';
								}}
							>
								{ml('Cancel', currentUser, allMultiLingualData)}
							</button>
							<button
								type="submit"
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									color: '#fff',
									background: accentColor,
									border: 'none',
									cursor: 'pointer',
									boxShadow: `0 1px 3px ${accentRgba(accentColor, 0.3)}`,
									transition: 'all 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = darkenHex(accentColor);
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = accentColor;
								}}
							>
								{isEditing
									? ml('Save Changes', currentUser, allMultiLingualData)
									: ml('Publish', currentUser, allMultiLingualData)}
							</button>
						</div>
					</div>
				</form>
			</motion.div>

			{/* Delete Tag Confirmation Popup */}
			{deleteTagTarget && (
				<div
					style={{
						position: 'fixed',
						inset: 0,
						zIndex: 60,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
					onClick={() => {
						if (!deleteTagLoading) {
							setDeleteTagTarget(null);
						}
					}}
				>
					<div
						style={{
							position: 'fixed',
							inset: 0,
							background: 'rgba(15,23,42,0.3)',
						}}
					/>
					<motion.div
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.15 }}
						onClick={(e) => e.stopPropagation()}
						style={{
							position: 'relative',
							background: '#fff',
							borderRadius: 16,
							padding: 24,
							width: 380,
							boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
							textAlign: 'center',
						}}
					>
						{deleteTagSuccess ? (
							<>
								<div
									style={{
										width: 40,
										height: 40,
										borderRadius: '50%',
										background: '#dcfce7',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										margin: '0 auto 12px',
										fontSize: 20,
									}}
								>
									✓
								</div>
								<p
									style={{
										color: '#1e293b',
										fontSize: 14,
										fontWeight: 600,
										margin: '0 0 4px',
									}}
								>
									{ml(
										'Tag has been successfully deleted.',
										currentUser,
										allMultiLingualData
									)}
								</p>
								<p
									style={{
										color: '#475569',
										fontSize: 13,
										margin: '0 0 16px',
									}}
								>
									"{deleteTagTarget}"
								</p>
								<button
									type="button"
									onClick={() => {
										setSelectedTags((prev) =>
											prev.filter((t) => t !== deleteTagTarget)
										);
										setDeleteTagTarget(null);
									}}
									style={{
										padding: '8px 20px',
										borderRadius: 10,
										fontSize: 13,
										fontWeight: 600,
										color: '#fff',
										background: accentColor,
										border: 'none',
										cursor: 'pointer',
									}}
								>
									{ml('Done', currentUser, allMultiLingualData)}
								</button>
							</>
						) : (
							<>
								<div
									style={{
										width: 40,
										height: 40,
										borderRadius: '50%',
										background: '#fef2f2',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										margin: '0 auto 12px',
									}}
								>
									<LuTrash2 size={18} color="#ef4444" />
								</div>
								<p
									style={{
										color: '#1e293b',
										fontSize: 14,
										fontWeight: 600,
										margin: '0 0 4px',
									}}
								>
									{ml('Delete tag', currentUser, allMultiLingualData)} "
									{deleteTagTarget}"?
								</p>
								<p
									style={{
										color: '#475569',
										fontSize: 13,
										margin: '0 0 20px',
									}}
								>
									{ml(
										'Deleting this tag will remove it from all announcements, are you sure?',
										currentUser,
										allMultiLingualData
									)}
								</p>
								<div style={{ display: 'flex', gap: 10 }}>
									<button
										type="button"
										onClick={() => setDeleteTagTarget(null)}
										disabled={deleteTagLoading}
										style={{
											flex: 1,
											padding: '8px 0',
											borderRadius: 10,
											fontSize: 13,
											fontWeight: 600,
											background: '#f1f5f9',
											color: '#475569',
											border: 'none',
											cursor: deleteTagLoading ? 'not-allowed' : 'pointer',
											opacity: deleteTagLoading ? 0.5 : 1,
										}}
									>
										{ml('Cancel', currentUser, allMultiLingualData)}
									</button>
									<button
										type="button"
										disabled={deleteTagLoading}
										onClick={async () => {
											setDeleteTagLoading(true);
											try {
												await onDeleteTag(deleteTagTarget);
												setDeleteTagSuccess(true);
											} catch (error) {
												console.error('Error deleting tag:', error);
												message.error('Failed to delete tag');
												setDeleteTagTarget(null);
											} finally {
												setDeleteTagLoading(false);
											}
										}}
										style={{
											flex: 1,
											padding: '8px 0',
											borderRadius: 10,
											fontSize: 13,
											fontWeight: 600,
											color: '#fff',
											background: '#ef4444',
											border: 'none',
											cursor: deleteTagLoading ? 'not-allowed' : 'pointer',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											gap: 6,
										}}
									>
										{deleteTagLoading ? (
											<>
												<span
													style={{
														width: 14,
														height: 14,
														border: '2px solid rgba(255,255,255,0.3)',
														borderTopColor: '#fff',
														borderRadius: '50%',
														display: 'inline-block',
														animation: 'tagDeleteSpin 0.6s linear infinite',
													}}
												/>
												{ml('Deleting...', currentUser, allMultiLingualData)}
											</>
										) : (
											ml('Delete Tag', currentUser, allMultiLingualData)
										)}
									</button>
								</div>
							</>
						)}
					</motion.div>
				</div>
			)}
		</div>
	);
}

export default AnnouncementForm;
